import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ADMIN_ROLE, User } from "@dto/User";
import { Popover } from "@ui-kit/Popover/Popover";
import { Menu, MenuItem } from "@ui-kit/Menu/Menu";
import { UserProfile } from "../UserProfile/UserProfile";
import { Avatar } from "@ui-kit/Avatar/Avatar";
import { useAppDispatch, useAppSelector } from "@store/store";
import { getAdminDomain, getDomain } from "@lib/getDomain";
import { selectAuthToken, signOutAction } from "@store/sessionReducer";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

interface UserAvatarProps {
  user: User;
}

const UserAvatar: FunctionComponent<UserAvatarProps> = ({ user }) => {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const authToken = useAppSelector(selectAuthToken);
  const { t } = useTranslation();
  const menuRef = useRef<any>(null);
  const buttonRef = useRef<any>(null);

  const dispatch = useAppDispatch();

  const profileMenuItems = useMemo(() => {
    return buildProfileMenuItems(user, t);
  }, [user, t]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        !menuRef.current?.contains(event.target) &&
        !buttonRef.current?.contains(event.target)
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelect = useCallback(
    (key: string) => {
      if (key === "profile") {
        setIsProfileOpen(true);
      } else if (key === "logout") {
        dispatch(signOutAction());
      } else if (key === "admin") {
        window.open(
          `${getAdminDomain()}/?client_id=${VisitWidget.settings.currentClientId}#auth_token=${authToken}`,
          "_blank",
        );
      }
      setIsMenuOpen(false);
    },
    [authToken, dispatch],
  );

  const handleCloseProfile = useCallback(() => {
    setIsProfileOpen(false);
  }, []);

  const handleAvatarPress = useCallback(() => {
    setIsMenuOpen(!isMenuOpen);
  }, [isMenuOpen]);

  return (
    <>
      <Popover
        isOpen={isMenuOpen}
        content={
          <div ref={menuRef}>
            <Menu items={profileMenuItems} onSelect={handleSelect} />
          </div>
        }>
        <a
          onClick={handleAvatarPress}
          className="user-avatar-component"
          ref={buttonRef}
          style={{
            cursor: "pointer",
          }}>
          <Avatar name={user.name} />
        </a>
      </Popover>
      <UserProfile
        user={user}
        isOpen={isProfileOpen}
        onClose={handleCloseProfile}
      />
    </>
  );
};

export { UserAvatar };

const buildProfileMenuItems = (user: User, t: TFunction) => {
  const items = [
    {
      label: t("screens.account.profile"),
      key: "profile",
    },
  ] as MenuItem[];

  if (user.roles?.includes(ADMIN_ROLE)) {
    items.push({
      label: t("screens.account.commandConsole"),
      key: "admin",
    });
  }

  items.push({
    label: t("screens.account.logout"),
    key: "logout",
    danger: true,
  });

  return items;
};
